// src/features/patchNotes/PatchNotesDialog.jsx
import React, { useState, useEffect, useMemo, useRef } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
  ListItemText,
  IconButton,
} from "@mui/material"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import CloseIcon from "@mui/icons-material/Close"
import pkg from "../../../package.json"
import { WW_BLUE } from "@/theme/tokens"
import { loadWelcomeSeen } from "../tutorials/tourProgress"
import { autoOpenDecision, loadLastSeen, saveLastSeen } from "./patchNotesSeen"

const MENU_VERSION_LIMIT = 5

function VersionSections({ entry }) {
  return (
    <>
      <Typography variant="subtitle2" sx={{ opacity: 0.6, mb: 1 }}>
        {entry.date}
      </Typography>
      {entry.sections.map((sec) => (
        <Box key={sec.title} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1, mb: 0.5 }}>
            {sec.title}
          </Typography>
          <ul style={{ marginTop: 0 }}>
            {sec.items.map((item, i) => (
              <li key={i}>
                <Typography
                  variant="body2"
                  component="span"
                  dangerouslySetInnerHTML={{
                    __html: item.replace(/^([^:]+):/, "<b>$1:</b>"),
                  }}
                />
              </li>
            ))}
          </ul>
        </Box>
      ))}
    </>
  )
}

export default function PatchNotesDialog() {
  const currentVersion = pkg.version
  const [openDialog, setOpenDialog] = useState(false)
  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0)

  const [anchorEl, setAnchorEl] = useState(null)
  const menuOpen = Boolean(anchorEl)
  const [showAllVersions, setShowAllVersions] = useState(false)

  // 🔹 Toolbar-Trigger-Ref für sauberes Fokus-Handling
  const triggerRef = useRef(null)

  useEffect(() => {
    const lastSeen = loadLastSeen()
    const welcomeSeen = loadWelcomeSeen()
    setLoading(true)
    fetch("/patchnotes/patch-notes.json", { cache: "no-cache" })
      .then((res) => {
        if (!res.ok) throw new Error("HTTP " + res.status)
        return res.json()
      })
      .then((data) => {
        setNotes(data)
        const idx = data.findIndex((n) => n.version === currentVersion)
        setSelectedVersionIndex(idx >= 0 ? idx : 0)

        const decision = autoOpenDecision({ notes: data, currentVersion, lastSeen, welcomeSeen })
        if (decision === "markSeen") saveLastSeen(currentVersion)
        if (decision === "open") setOpenDialog(true)
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [currentVersion])

  const selectedEntry = useMemo(() => {
    if (!notes || notes.length === 0) return null
    return notes[selectedVersionIndex]
  }, [notes, selectedVersionIndex])

  // 🔹 Öffnen/Schließen Menü + Fokus managen
  const openMenu = (e) => setAnchorEl(e.currentTarget)

  const closeMenu = () => {
    // erst Fokus von evtl. fokussiertem MenuItem nehmen
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur()
    }
    setAnchorEl(null)
    // eigentlichen Fokus zurück auf Trigger setzen wir nach der Transition (siehe TransitionProps.onExited)
  }

  const handleSelectVersion = (index) => {
    setSelectedVersionIndex(index)
    setShowAllVersions(false)
    closeMenu()
    setOpenDialog(true)
  }

  const handleViewAll = () => {
    setShowAllVersions(true)
    closeMenu()
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    saveLastSeen(currentVersion)
    setOpenDialog(false)
    setShowAllVersions(false)
    // nach Dialog schließen Fokus zurück auf Trigger
    if (triggerRef.current) {
      triggerRef.current.focus()
    }
  }

  // 🔹 Tastaturbedienung für den Trigger (Enter/Space)
  const onTriggerKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      openMenu(e)
    }
  }

  return (
    <>
      {/* === Toolbar Dropdown (weiß, immer aktuelle Build-Version) === */}
      <Box
        ref={triggerRef}
        tabIndex={0}
        aria-label="Open patch notes menu"
        onKeyDown={onTriggerKeyDown}
        onClick={openMenu}
        role="button"
        aria-haspopup="menu"
        aria-controls={menuOpen ? "versions-menu" : undefined}
        aria-expanded={menuOpen ? "true" : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          pr: 0,
          pl: 0.8,
          py: 0.5,
          ml: 2,
          mr: 0,
          borderRadius: 1,
          bgcolor: "white",
          cursor: "pointer",
          userSelect: "none",
          color: "#004f7d",
          fontWeight: "bold",
          "&:hover": { bgcolor: "action.selected" },
        }}
      >
        <Typography variant="subtitle2" sx={{ lineHeight: 1.2, mr: 0 }}>
          v{currentVersion}
        </Typography>
        <ArrowDropDownIcon fontSize="small" />
      </Box>

      <Menu
        id="versions-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        keepMounted
        slotProps={{
          transition: {
            onExited: () => {
              // Fokus erst NACH dem Unmount des Menüs zurück auf den Trigger
              if (triggerRef.current) triggerRef.current.focus()
            },
          },
          list: {
            autoFocusItem: false, // ehemals MenuListProps
          },
        }}
      >
        {notes.slice(0, MENU_VERSION_LIMIT).map((n, i) => (
          <MenuItem key={n.version} onClick={() => handleSelectVersion(i)} selected={i === selectedVersionIndex}>
            <ListItemText primary={`v${n.version}`} slotProps={{ primary: { sx: { fontSize: 14 } } }} />
          </MenuItem>
        ))}

        <Divider />

        <MenuItem onClick={handleViewAll}>
          <ListItemText primary="View all versions" slotProps={{ primary: { sx: { fontSize: 14 } } }} />
        </MenuItem>
      </Menu>

      {/* === Dialog === */}
      <Dialog open={openDialog} onClose={handleCloseDialog} disableRestoreFocus aria-labelledby="patch-notes-title" maxWidth="sm" fullWidth>
        <DialogTitle id="patch-notes-title" sx={{ pr: 6 }}>
          {showAllVersions
            ? "Patch Notes – All Versions"
            : `Patch Notes – v${selectedEntry?.version ?? currentVersion}`}
        </DialogTitle>
        <IconButton
          aria-label="Close patch notes"
          onClick={handleCloseDialog}
          size="small"
          sx={{ position: "absolute", top: 12, right: 12, color: WW_BLUE }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <DialogContent dividers sx={{ minHeight: 200 }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && <Typography color="error">Failed to load patch notes: {error}</Typography>}

          {/* Einzelversion */}
          {!loading && !error && notes.length > 0 && !showAllVersions && selectedEntry && (
            <VersionSections entry={selectedEntry} />
          )}

          {/* Alle Versionen */}
          {!loading && !error && notes.length > 0 && showAllVersions && (
            <Box>
              {notes.map((entry) => (
                <Box key={entry.version} sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    v{entry.version}
                  </Typography>
                  <VersionSections entry={entry} />
                  <Divider />
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
